import React, { Suspense } from 'react'

import { Switch, RouteComponentProps, Route, Redirect } from 'react-router-dom'

import { compose } from 'lodash/fp'
import { AppContext, AppContextData } from './contexts/AppContext'
import { Loading } from '@elgatoylacaja/components'
import Intro from './views/Intro'
import Step1 from './views/Steps/Step1'
import Step2 from './views/Steps/Step2'
import Step3 from './views/Steps/Step3'
import Step4 from './views/Steps/Step4'

const LazyDemoRoutes = React.lazy(() =>
  import(/* webpackChunkName: "DemoRoutes" */ './DemoRoutes')
)

const Regressors = React.lazy(() =>
  import(/* webpackChunkName: "Regressors" */ './views/Regressors')
)

const Terms = React.lazy(() =>
  import(/* webpackChunkName: "Terms" */ './views/Terms')
)

const Thanks = React.lazy(() =>
  import(/* webpackChunkName: "Thanks" */ './views/Thanks')
)

type WrappedComponentType =
  | React.ComponentType<RouteComponentProps<any>>
  | React.ComponentType<any>

const condition = (appState: AppContextData) => (
  f: (x: AppContextData) => boolean
) => (r: string) => (WrappedComponent: WrappedComponentType) => (
  p: RouteComponentProps
) => {
  return p.match.isExact ? (
    f(appState) ? (
      <WrappedComponent {...p} />
    ) : (
      <Redirect to={r} {...p} />
    )
  ) : null
}

type LinkedRouteConfig = { prev?: string; route: string; next?: string }

class LinkedRoutes {
  paths: Array<[string, React.ComponentType<any>]>

  constructor(paths: Array<[string, React.ComponentType<any>]>) {
    this.paths = paths
  }

  config = () =>
    this.paths.map(([path, component], index, list): [
      LinkedRouteConfig,
      React.ComponentType
    ] => [
      {
        prev: index === 0 ? undefined : list[index - 1][0],
        route: path,
        next: index === list.length - 1 ? undefined : list[index + 1][0],
      },
      component,
    ])
}

const ExperimentRoutes = new LinkedRoutes([
  ['/terms', Terms],
  ['/intro', Intro],
  ['/step-1', Step1],
  ['/step-2', Step2],
  ['/step-3', Step3],
  ['/step-4', Step4],
  ['/regressors', Regressors],
  ['/thanks', Thanks],
]).config()

const LazySwitchRoutes = () => {
  const { appState } = React.useContext(AppContext)

  const stateCondition = condition(appState)

  const alwaysTrue = stateCondition(_ => true)('')

  const visited = (r: string) =>
    stateCondition(state => state.visited.includes(r))(r)

  const notVisited = (r: string) =>
    stateCondition(state => !state.visited.includes(r))

  const requirePrevious = ({ prev, route, next }: LinkedRouteConfig) =>
    compose(
      prev !== undefined ? visited(prev) : alwaysTrue,
      next !== undefined ? notVisited(route)(next) : alwaysTrue
    )

  return (
    <Switch>
      <Redirect from="/" exact to="/terms" />
      <Suspense fallback={<Loading loading fixed />}>
        {ExperimentRoutes.map(([config, component]) => (
          <Route
            key={config.route}
            path={config.route}
            exact
            render={requirePrevious(config)(component)}
          />
        ))}

        {process.env.REACT_APP_ENVIRONMENT !== 'production' ? (
          <LazyDemoRoutes appState={appState} />
        ) : null}
      </Suspense>
    </Switch>
  )
}

export default LazySwitchRoutes
